import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    catchError,
    combineLatestWith,
    filter,
    forkJoin,
    map,
    mergeMap,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs'
import { Store } from '@ngrx/store'
import { selectionActions, noOpAction } from './selection.actions'
import {
    selectFreeSelection,
    selectHierarchicalSelection,
    selectSavedFreeSelections,
    selectSavedHierarchicalSelections,
    selectSavedUnsupervisedSelections,
    selectUnsupervisedSelection,
} from './selection.selectors'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { selectCaseId } from '../case/case.selectors'
import { ToasterService } from '../../shared/services/toaster.service'
import { defaultDebounceTime } from '../../shared/utils/rxjs-pipelines'
import { RouterUtilsService } from '../../shared/services/router-utils.service'
import { HierarchicalSelectionRoot, SelectionMode, SelectionType } from './selection.state'
import { viewerActions } from '../viewer/viewer.actions'

@Injectable()
export class SelectionEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly datasetApi: DatasetApiService,
        private readonly toaster: ToasterService,
        private readonly routerUtils: RouterUtilsService
    ) {}

    loadFreeSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) => {
                return this.datasetApi.getFreeSelectionsForCase(caseId).pipe(
                    combineLatestWith(this.routerUtils.getFreeSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        return selectionActions.loadingFreeSelectionsSucceeded({
                            selections,
                            queryParams,
                        })
                    }),
                    catchError(error =>
                        of(
                            selectionActions.loadingFreeSelectionsFailed({
                                error,
                                message: 'Error loading selections for dataset.',
                            })
                        )
                    )
                )
            })
        )
    })

    loadHierarchicalSelectionList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) =>
                this.datasetApi.getHierarchicalSelectionListForCase(caseId).pipe(
                    map(hierarchicalSelectionList =>
                        selectionActions.loadingHierarchicalSelectionListSucceeded({
                            list: hierarchicalSelectionList,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.loadingHierarchicalSelectionListFailed({
                                error,
                                message: 'Error loading hierarchical selections for dataset.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadUnsupervisedSelectionList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(viewerActions.configLoaded),
            combineLatestWith(this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            switchMap(([, caseId]) =>
                this.datasetApi.getUnsupervisedSelectionListForCase(caseId).pipe(
                    map(unsupervisedSelectionList =>
                        selectionActions.loadingUnsupervisedSelectionListSucceeded({
                            list: unsupervisedSelectionList,
                        })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.loadingUnsupervisedSelectionListFailed({
                                error,
                                message: 'Error loading unsupervised selections for dataset.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadHierarchicalSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.loadingHierarchicalSelectionListSucceeded),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            mergeMap(([{ list }, caseId]) => {
                if (list.length === 0) {
                    return of(noOpAction())
                }
                const apiCalls = list.map(name => {
                    return this.datasetApi.getSingleHierarchicalSelectionForCase(caseId, name).pipe(
                        take(1),
                        catchError(error =>
                            of(
                                selectionActions.loadingHierarchicalSelectionsFailed({
                                    error,
                                    message: 'Error loading hierarchical selection.',
                                })
                            )
                        )
                    )
                })
                return forkJoin(apiCalls).pipe(
                    combineLatestWith(this.routerUtils.getHierarchicalSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        const combinedSelections = new Map<string, HierarchicalSelectionRoot>()
                        selections.forEach(selection => {
                            if ('error' in selection) {
                                return
                            }
                            for (const [key, value] of selection) {
                                combinedSelections.set(key, value)
                            }
                        })
                        return selectionActions.loadingHierarchicalSelectionsSucceeded({
                            selections: combinedSelections,
                            queryParams: queryParams,
                        })
                    })
                )
            })
        )
    })

    loadUnsupervisedSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.loadingUnsupervisedSelectionListSucceeded),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            mergeMap(([{ list }, caseId]) => {
                if (list.length === 0) {
                    return of(noOpAction())
                }
                const apiCalls = list.map(name => {
                    return this.datasetApi.getSingleUnsupervisedSelectionForCase(caseId, name).pipe(
                        take(1),
                        catchError(error =>
                            of(
                                selectionActions.loadingUnsupervisedSelectionsFailed({
                                    error,
                                    message: 'Error loading unsupervised selection.',
                                })
                            )
                        )
                    )
                })
                return forkJoin(apiCalls).pipe(
                    combineLatestWith(this.routerUtils.getUnsupervisedSelectionQueryParams()),
                    take(1),
                    map(([selections, queryParams]) => {
                        const combinedSelections = new Map<string, HierarchicalSelectionRoot>()
                        selections.forEach(selection => {
                            if ('error' in selection) {
                                return
                            }
                            for (const [key, value] of selection) {
                                combinedSelections.set(key, value)
                            }
                        })
                        return selectionActions.loadingUnsupervisedSelectionsSucceeded({
                            selections: combinedSelections,
                            queryParams: queryParams,
                        })
                    })
                )
            })
        )
    })

    saveFreeSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.saveNewFreeSelection, selectionActions.saveFreeSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectFreeSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.saveFreeSelection(caseId!, selection).pipe(
                    map(() => selectionActions.savingFreeSelectionSucceeded({ selection })),
                    catchError(error =>
                        of(
                            selectionActions.savingFreeSelectionFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    saveHierarchicalSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionNodeRecursively
            ),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectHierarchicalSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.saveHierarchicalSelection(caseId!, selection).pipe(
                    map(() =>
                        selectionActions.savingHierarchicalSelectionRootSucceeded({ selection })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.savingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    saveUnsupervisedSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.saveNewAutoCellTypeDetectionRoot,
                selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.deleteAutoCellTypeDetectionNode,
                selectionActions.deleteAutoCellTypeDetectionNodeRecursively
            ),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectUnsupervisedSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.saveUnsupervisedSelection(caseId!, selection).pipe(
                    map(() =>
                        selectionActions.savingAutoCellTypeDetectionRootSucceeded({ selection })
                    ),
                    catchError(error =>
                        of(
                            selectionActions.savingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error saving selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    deleteCurrentFreeSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteFreeSelection),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectFreeSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.deleteFreeSelection(caseId!, selection.name!).pipe(
                    map(() => selectionActions.deletingFreeSelectionSucceeded()),
                    catchError(error =>
                        of(
                            selectionActions.deletingFreeSelectionFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    deleteCurrentHierarchicalSelectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteHierarchicalSelectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectHierarchicalSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.deleteHierarchicalSelection(caseId!, selection.name!).pipe(
                    map(() => selectionActions.deletingHierarchicalSelectionRootSucceeded()),
                    catchError(error =>
                        of(
                            selectionActions.deletingHierarchicalSelectionRootFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    deleteCurrentAutoCellTypeDetectionRoot$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.deleteAutoCellTypeDetectionRoot),
            concatLatestFrom(() => this.store.select(selectCaseId)),
            filter(([, caseId]) => !!caseId),
            concatLatestFrom(() => this.store.select(selectUnsupervisedSelection)),
            switchMap(([[, caseId], selection]) => {
                return this.datasetApi.deleteUnsupervisedSelection(caseId!, selection.name!).pipe(
                    map(() => selectionActions.deletingAutoCellTypeDetectionRootSucceeded()),
                    catchError(error =>
                        of(
                            selectionActions.deletingAutoCellTypeDetectionRootFailed({
                                error,
                                message: 'Error deleting selection.',
                            })
                        )
                    )
                )
            })
        )
    })

    showErrorToast$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.loadingFreeSelectionsFailed,
                    selectionActions.loadingHierarchicalSelectionListFailed,
                    selectionActions.loadingHierarchicalSelectionsFailed,
                    selectionActions.savingFreeSelectionFailed,
                    selectionActions.savingHierarchicalSelectionRootFailed,
                    selectionActions.deletingFreeSelectionFailed,
                    selectionActions.deletingHierarchicalSelectionRootFailed
                ),
                tap(({ error, message }) => {
                    this.toaster.httpError(error, message)
                })
            )
        },
        { dispatch: false }
    )

    // setQueryParamSelections$ = createEffect(
    //     () => {
    //         return this.actions$.pipe(
    //             ofType(
    //                 selectionActions.setSelectionOpacity,
    //                 selectionActions.setSelectionVisibility,
    //                 selectionActions.setSelectionShaderColor
    //             ),
    //             defaultDebounceTime(),
    //             concatLatestFrom(() => this.store.select(selectSavedFreeSelections)),
    //             concatLatestFrom(() => this.store.select(selectSavedHierarchicalSelections)),
    //             concatLatestFrom(() => this.store.select(selectSavedUnsupervisedSelections)),
    //             tap(([[[, selections], hierarchicalSelections], unsupervisedSelections]) => {
    //                 this.routerUtils.updateSelectionQueryParam(
    //                     selections,
    //                     hierarchicalSelections,
    //                     unsupervisedSelections
    //                 )
    //             })
    //         )
    //     },
    //     { dispatch: false }
    // )

    showSelectionOnSwitch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectionActions.setFreeSelection),
            map(({ name }) => {
                return selectionActions.setSelectionVisibility({
                    selectionName: name,
                    visible: true,
                    selectionType: SelectionType.Free,
                    selectionRoot: undefined,
                })
            })
        )
    })

    resetSelectionMode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.setFreeSelection,
                selectionActions.setHierarchicalSelectionNode,
                selectionActions.setHierarchicalSelectionRoot,
                selectionActions.setAutoCellTypeDetectionNode,
                selectionActions.setAutoCellTypeDetectionRoot,
                selectionActions.deleteFreeSelection,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionRoot,
                selectionActions.deleteHierarchicalSelectionNodeRecursively,
                selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewFreeSelection,
                selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.saveNewAutoCellTypeDetectionRoot
            ),
            map(() => selectionActions.setMode({ mode: SelectionMode.Add }))
        )
    })

    resetVisibleSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                selectionActions.setFreeSelection,
                selectionActions.setHierarchicalSelectionNode,
                selectionActions.setHierarchicalSelectionRoot,
                selectionActions.setAutoCellTypeDetectionNode,
                selectionActions.setAutoCellTypeDetectionRoot,
                selectionActions.deleteFreeSelection,
                selectionActions.deleteHierarchicalSelectionNode,
                selectionActions.deleteHierarchicalSelectionRoot,
                selectionActions.deleteHierarchicalSelectionNodeRecursively,
                // selectionActions.saveNewHierarchicalSelectionNode,
                selectionActions.saveNewHierarchicalSelectionRoot,
                selectionActions.saveNewFreeSelection,
                // selectionActions.saveNewAutoCellTypeDetectionNode,
                selectionActions.saveNewAutoCellTypeDetectionRoot
            ),
            map(() => selectionActions.setVisibleSelectionsOnScatterPlot({ selectionNames: [] }))
        )
    })

    updateSelections$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.setSelectionOpacity,
                    selectionActions.setSelectionVisibility,
                    selectionActions.setSelectionShaderColor
                ),
                defaultDebounceTime(),
                this.datasetApi.saveViewerState(),
                filter(({ selectionName }) => !!selectionName),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                concatLatestFrom(() => this.store.select(selectSavedFreeSelections)),
                concatLatestFrom(() => this.store.select(selectSavedHierarchicalSelections)),
                concatLatestFrom(() => this.store.select(selectSavedUnsupervisedSelections)),
                switchMap(([[[[action, caseId], freeSelections], hierarchicalSelections]]) => {
                    if (action.selectionType == SelectionType.Free) {
                        if (!action.selectionName) return of()
                        const updatedSelection = freeSelections.get(action.selectionName)
                        if (!updatedSelection) return of()
                        return this.datasetApi.saveFreeSelection(caseId!, updatedSelection)
                    } else if (action.selectionType == SelectionType.Hierarchical) {
                        if (!action.selectionRoot) return of()
                        const updatedSelection = hierarchicalSelections.get(action.selectionRoot)
                        if (!updatedSelection) return of()
                        return this.datasetApi.saveHierarchicalSelection(caseId!, updatedSelection)
                    } else if (action.selectionType == SelectionType.Unsupervised) {
                        if (!action.selectionRoot) return of()
                        const updatedSelection = hierarchicalSelections.get(action.selectionRoot)
                        if (!updatedSelection) return of()
                        return this.datasetApi.saveHierarchicalSelection(caseId!, updatedSelection)
                    } else {
                        return of()
                    }
                })
            )
        },
        { dispatch: false }
    )
}
